import React, { useState } from "react";
import {
  Sun,
  ArrowRight,
  CheckCircle2,
  PlayCircle,
  Quote,
  MapPin,
  Compass,
  FileCheck2,
  CalendarClock,
  HeartHandshake,
  Stethoscope,
  Link2,
  ChevronDown,
  Home,
  ClipboardList,
  Landmark,
  GraduationCap,
} from "lucide-react";
import { PHOTOS, SPAIN_VILLAGE, EU_BOOKING_URL } from "@/lib/site-config";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BookSection from "@/components/site/BookSection";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";
import usePageMeta from "@/lib/use-page-meta";

/* ---------------- HERO ---------------- */
const Hero = () => (
  <section id="top" className="relative pt-28 pb-20 lg:pt-40 lg:pb-28 overflow-hidden editorial">
    <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />
    <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="fade-up inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium" data-testid="eu-hero-badge">
          <Sun className="w-3.5 h-3.5" />
          EU Approved · Personalised Relocation Support
        </div>

        <h1
          className="fade-up delay-100 font-serif text-[2.4rem] sm:text-[3rem] lg:text-[3.8rem] text-[var(--navy)] mt-6 leading-[1.05]"
          data-testid="eu-hero-headline"
        >
          Your EU passport opens the door.{" "}
          <em className="text-[var(--terracotta)]">We help you through everything that comes next.</em>
        </h1>

        <p
          className="fade-up delay-200 mt-7 text-lg lg:text-xl text-[var(--ink-soft)] max-w-xl leading-relaxed"
          data-testid="eu-hero-subhead"
        >
          A personal, step-by-step relocation service for EU citizens and their families moving to
          Spain. Know what you need, when you need it and what to do next — from planning your move
          through to preparing for residency.
        </p>

        <div className="fade-up delay-300 mt-10 flex flex-wrap gap-4" data-testid="eu-hero-ctas">
          <a
            href="#book"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
            data-testid="eu-hero-cta-primary"
          >
            Plan my move to Spain <ArrowRight className="w-5 h-5" />
          </a>
          <a
            href="#what-you-get"
            className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium"
            data-testid="eu-hero-cta-secondary"
          >
            <PlayCircle className="w-5 h-5" /> See how EU Approved works
          </a>
        </div>

        <div className="fade-up delay-400 mt-12 flex items-center gap-6 text-sm text-[var(--muted)]">
          <div className="flex -space-x-3">
            {[PHOTOS.carley_solo, PHOTOS.carley_daughter, PHOTOS.carley_son].map((src, i) => (
              <img key={i} src={src} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-[var(--cream)]" />
            ))}
          </div>
          <div>
            <span className="block text-[var(--ink)] font-semibold">Personal support from Carley · Built around your circumstances</span>
            <span className="block text-xs">Available wherever you are moving in Spain</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5 relative fade-in delay-300">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
          <img src={PHOTOS.carley_sunset} alt="Carley enjoying life in Spain" className="absolute inset-0 w-full h-full object-cover slow-zoom" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy)]/45 via-transparent to-transparent" />
          <div className="absolute top-6 left-6 right-6 text-[var(--cream)]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-2 opacity-90">
              <MapPin className="w-3.5 h-3.5" /> Living the plan · Spain
            </div>
          </div>
        </div>
        <div className="hidden lg:block absolute -bottom-12 -left-16 w-44 h-56 rounded-2xl overflow-hidden shadow-2xl rotate-[-6deg] border-[6px] border-[var(--cream)]">
          <img src={PHOTOS.family_beach_sunset} alt="A family settled in Spain" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- REALITY CHECK ---------------- */
const RealityCheck = () => (
  <section className="relative py-24 lg:py-32 bg-[var(--navy)] text-[var(--cream)] overflow-hidden grain editorial">
    <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
      <img src={SPAIN_VILLAGE} alt="" className="w-full h-full object-cover" />
    </div>
    <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
      <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium" data-testid="eu-reality-eyebrow">
        Your passport is the starting point
      </div>
      <h2 className="font-serif text-4xl lg:text-5xl mt-6 leading-[1.08]" data-testid="eu-reality-headline">
        Moving to Spain with an EU passport should be simple.{" "}
        <em className="text-[var(--gold)]">Until you start looking into it.</em>
      </h2>
      <p className="mt-10 text-[1.05rem] leading-relaxed text-[var(--cream)]/85 max-w-3xl">
        Your passport may give you the right to live in Spain. It does not automatically tell you
        what paperwork to prepare, what needs translating or apostilling, how to arrange healthcare,
        how to demonstrate your circumstances, when specialist tax advice may matter, how to
        register locally or what a non-EU family member needs to do. And the more people you ask,
        the more different answers you can receive. EU Approved replaces the guesswork with a
        personalised plan.
      </p>
    </div>
  </section>
);

/* ---------------- BUYER LANGUAGE ---------------- */
const BuyerLanguage = () => (
  <section className="py-24 lg:py-28 editorial">
    <div className="max-w-5xl mx-auto px-6 lg:px-10">
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.08]" data-testid="eu-buyer-headline">
        Tired of getting a different answer from{" "}
        <em className="text-[var(--terracotta)]">everyone you ask?</em>
      </h2>
      <div className="mt-8 text-[1.05rem] leading-relaxed text-[var(--ink-soft)] space-y-5 max-w-3xl">
        <p>
          Friends, Facebook groups and online checklists can all be useful. The problem is that they
          rarely know your whole situation. Are you moving alone or with a partner? Is one family
          member not an EU citizen? Are you retired, working or using savings? Will your
          accommodation allow local registration? Do you need an S1 or private healthcare? Are you
          selling a property before you move?
        </p>
        <p>
          The correct next step can depend on the answers. EU Approved helps you stop collecting
          generic information and start following a plan built around your move.
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- CORE PROPOSITION ---------------- */
const CoreProposition = () => {
  const items = [
    {
      icon: Compass,
      title: "Your personal relocation plan",
      body: "A clear sequence based on your passport situation, family, timeline, work, finances, healthcare and intended destination.",
    },
    {
      icon: FileCheck2,
      title: "Document preparation and checking",
      body: "Understand which documents may be relevant, how recent they need to be and whether translation, apostille or supporting evidence may be required.",
    },
    {
      icon: CalendarClock,
      title: "Appointment readiness",
      body: "Prepare the forms, evidence, payments and practical information you are likely to need before attending an appointment.",
    },
    {
      icon: HeartHandshake,
      title: "Direct access to Carley",
      body: "Ask questions as your plans develop instead of returning to forums and starting the research again.",
    },
    {
      icon: Stethoscope,
      title: "Healthcare and practical setup",
      body: "Understand the likely healthcare route and how accommodation, local registration and other practical decisions connect with residency.",
    },
    {
      icon: Link2,
      title: "Trusted specialist connections",
      body: "Where appropriate, receive introductions to relevant providers such as sworn translators, apostille services, healthcare brokers and cross-border tax or legal specialists.",
    },
  ];

  return (
    <section id="what-you-get" className="py-24 lg:py-32 bg-[var(--cream-deep)]/60 relative editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
              What EU Approved gives you
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="eu-proposition-headline">
              Know exactly what to do, when to do it and{" "}
              <em>what needs to happen next.</em>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[var(--ink-soft)] text-lg">
            This is not simply a list of documents. It is personal oversight of the move: planning,
            preparation, checking, appointment readiness and support as you work through the
            relevant stages.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {items.map((it, i) => (
            <div key={i} className="soft-card rounded-2xl p-7 hover:bg-white transition-colors duration-500 group" data-testid={`eu-proposition-item-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[var(--terracotta)]/10 text-[var(--terracotta)] flex items-center justify-center mb-5 group-hover:bg-[var(--terracotta)] group-hover:text-[var(--cream)] transition-colors duration-500">
                <it.icon className="w-5 h-5" strokeWidth={2} />
              </div>
              <h3 className="font-serif text-2xl text-[var(--navy)] leading-tight">{it.title}</h3>
              <p className="mt-3 text-[var(--ink-soft)] text-[0.95rem] leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- SEQUENCE VISUAL ---------------- */
const SequenceVisual = () => {
  const steps = [
    { icon: Home, label: "Accommodation" },
    { icon: ClipboardList, label: "Padrón / local registration" },
    { icon: Landmark, label: "Residency" },
    { icon: GraduationCap, label: "Schooling & other registrations" },
  ];
  return (
    <section className="py-24 lg:py-28 bg-[var(--navy)] text-[var(--cream)] relative overflow-hidden editorial grain" data-testid="eu-sequence">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium">
          Your move is not a checklist
        </div>
        <h2 className="font-serif text-4xl lg:text-5xl mt-6 leading-[1.08]" data-testid="eu-sequence-headline">
          One decision can affect <em className="text-[var(--gold)]">three things that follow.</em>
        </h2>
        <p className="mt-8 text-[1.05rem] leading-relaxed text-[var(--cream)]/85 max-w-3xl">
          Short-term accommodation may affect whether you can register locally. Local registration
          can affect residency and schooling. Your move date may affect when documents should be
          ordered and when specialist tax advice is sensible.
        </p>

        <div className="mt-14 flex flex-col lg:flex-row items-stretch lg:items-center gap-4" data-testid="eu-sequence-path">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className="flex-1 rounded-2xl p-6 bg-[var(--navy-deep)]/60 border border-[var(--cream)]/10 flex items-center gap-4 lg:flex-col lg:text-center lg:gap-3">
                <span className="w-12 h-12 rounded-full bg-[var(--gold)]/15 text-[var(--gold)] flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-6 h-6" />
                </span>
                <span className="font-serif text-lg leading-tight">{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <ArrowRight className="w-6 h-6 text-[var(--gold)] flex-shrink-0 self-center rotate-90 lg:rotate-0" />
              )}
            </React.Fragment>
          ))}
        </div>
        <p className="mt-8 text-sm text-[var(--cream)]/70 italic">
          EU Approved helps you understand those dependencies before they become last-minute problems.
        </p>
      </div>
    </section>
  );
};

/* ---------------- FOUR-STAGE JOURNEY ---------------- */
const Journey = () => {
  const stages = [
    { n: "01", title: "Plan your move", body: "Map your circumstances, intended timeline, likely route, important risks and the decisions that need making early." },
    { n: "02", title: "Prepare before leaving", body: "Organise relevant documents, translations, apostilles, financial evidence, healthcare and accommodation considerations at the appropriate time." },
    { n: "03", title: "Move to Spain", body: "Arrive knowing which registrations and appointments come first, what to take and what should already be in place." },
    { n: "04", title: "Settle successfully", body: "Work through the relevant residency and practical registrations, with introductions to specialist help when needed." },
  ];
  return (
    <section className="py-24 lg:py-32 editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05] max-w-3xl" data-testid="eu-journey-headline">
          A properly planned move, from <em className="text-[var(--terracotta)]">first decision to settling in.</em>
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {stages.map((s, i) => (
            <div key={i} className="soft-card rounded-2xl p-7" data-testid={`eu-stage-${i}`}>
              <div className="font-serif text-4xl text-[var(--terracotta)]/60">{s.n}</div>
              <h3 className="font-serif text-2xl text-[var(--navy)] mt-4 leading-tight">{s.title}</h3>
              <p className="mt-3 text-[var(--ink-soft)] text-[0.95rem] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- DIY + TRIGGER ---------------- */
const DiyAndTrigger = () => (
  <section className="py-24 lg:py-28 bg-[var(--cream-deep)]/60 editorial">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-8">
      <div className="rounded-[2rem] p-8 lg:p-10 bg-white border border-black/5" data-testid="eu-diy-card">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
          Already done the research?
        </div>
        <h3 className="font-serif text-3xl text-[var(--navy)] mt-4 leading-[1.1]">
          Even well-prepared people <em className="text-[var(--terracotta)]">miss things.</em>
        </h3>
        <p className="mt-5 text-[var(--ink-soft)] leading-relaxed">
          You can spend months researching, collect your paperwork and still discover that something
          is missing, too old, in the wrong format or needs to happen in a different order. EU
          Approved does not disregard the work you have already done. It helps check it, identify
          gaps and turn your research into a workable plan.
        </p>
      </div>
      <div className="rounded-[2rem] p-8 lg:p-10 bg-[var(--navy)] text-[var(--cream)]" data-testid="eu-trigger-card">
        <h3 className="font-serif text-3xl leading-[1.1]">
          If your house sold tomorrow, would you know{" "}
          <em className="text-[var(--gold)]">what happens next?</em>
        </h3>
        <p className="mt-5 text-[var(--cream)]/85 leading-relaxed">
          Many people wait for a house sale, retirement date or family decision before preparing.
          Some documents should not be ordered too early because they have useful validity windows.
          But waiting does not mean you need to remain unsure. EU Approved helps you understand what
          can be planned now and what should wait, so you are ready to act when the move becomes
          real.
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- CIRCUMSTANCES ---------------- */
const Circumstances = () => {
  const items = [
    "Retiring to Spain with pensions, savings or employed income.",
    "Moving with an EU spouse or partner and a non-EU family member.",
    "Relocating with children and planning schooling.",
    "Working, self-employed or considering work in Spain.",
    "Selling property before moving.",
    "Already owning a home in Spain.",
    "Moving to Valencia, Alicante, Murcia, Málaga, Barcelona or elsewhere in Spain.",
  ];
  return (
    <section className="py-24 lg:py-28 editorial">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
            Your move will not look exactly like someone else's
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.08]" data-testid="eu-circumstances-headline">
            Support built around real lives, <em>not a standard checklist.</em>
          </h2>
          <ul className="mt-9 space-y-4">
            {items.map((t, i) => (
              <li key={i} className="flex gap-3 text-[var(--ink-soft)]">
                <CheckCircle2 className="w-5 h-5 text-[var(--terracotta)] flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 relative">
          <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden shadow-xl">
            <img src={PHOTOS.family_palms} alt="A family enjoying life in Spain" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-8 -left-6 w-40 h-52 rounded-2xl overflow-hidden shadow-xl border-4 border-[var(--cream)] rotate-[-5deg] hidden sm:block">
            <img src={PHOTOS.carley_beach_solo} alt="Carley on the beach in Spain" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------------- REMOTE + COST OF ERROR ---------------- */
const RemoteAndCost = () => (
  <section className="py-24 lg:py-28 bg-[var(--navy)] text-[var(--cream)] editorial grain relative overflow-hidden">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 space-y-16">
      <div data-testid="eu-remote-section">
        <h2 className="font-serif text-3xl lg:text-4xl leading-[1.12]">
          Moving somewhere Carley is not based?{" "}
          <em className="text-[var(--gold)]">That is built into the service.</em>
        </h2>
        <p className="mt-6 text-[var(--cream)]/85 leading-relaxed max-w-3xl">
          EU Approved is designed to support moves across Spain. The planning, document
          preparation, checking, form guidance and appointment preparation can be handled remotely.
          Where a step genuinely requires local or regulated expertise, Carley can help you identify
          an appropriate provider where available.
        </p>
      </div>
      <div className="border-t border-[var(--cream)]/15 pt-14" data-testid="eu-cost-section">
        <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium">
          Preparation costs less than repeating the process
        </div>
        <h2 className="font-serif text-3xl lg:text-4xl mt-5 leading-[1.12]">
          Buying the house is often <em className="text-[var(--gold)]">the easy bit.</em>
        </h2>
        <p className="mt-6 text-[var(--cream)]/85 leading-relaxed max-w-3xl">
          One incorrect document can mean another appointment. One missing apostille can mean
          sending paperwork back to the country of issue. A poorly timed decision can create
          avoidable expense or delay. EU Approved is designed to help you prepare before those
          problems happen. It cannot guarantee an authority's decision, but it can replace crossed
          fingers with a much clearer plan.
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- ABOUT ---------------- */
const About = () => (
  <section id="about" className="py-24 lg:py-32 relative overflow-hidden editorial">
    <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-[var(--gold)]/10 blur-3xl pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5 relative">
        <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-xl">
          <img src={PHOTOS.family_yacht} alt="Carley with her family" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-10 -right-6 lg:-right-12 w-44 h-56 rounded-2xl overflow-hidden shadow-xl border-4 border-[var(--cream)] rotate-[5deg]">
          <img src={PHOTOS.carley_son} alt="Carley with her son in Spain" className="w-full h-full object-cover" />
        </div>
        <span className="tape -top-3 left-12" />
      </div>

      <div className="lg:col-span-7">
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Meet your guide</div>
        <h2 className="font-serif text-4xl lg:text-6xl text-[var(--navy)] mt-5 leading-[1.02]" data-testid="eu-about-headline">
          Hi, I'm <em>Carley.</em>
        </h2>
        <div className="mt-6 space-y-5 text-[var(--ink-soft)] text-[1.05rem] leading-relaxed">
          <p>
            I built <strong className="text-[var(--navy)]">EU Approved</strong> because an EU passport can make moving
            to Spain possible without making the practical process obvious. I kept meeting people
            who had spent months researching and were still asking the same questions:
          </p>
          <p className="font-serif italic text-xl text-[var(--navy)]">
            What do we do first? Which documents apply to us? What happens when we arrive? Who do we
            see, and where do we go?
          </p>
          <p>
            My role is to help turn those questions into a calm, personalised plan — then support
            you as you work through it.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- PROOF ---------------- */
const Proof = () => (
  <section id="stories" className="py-24 lg:py-28 bg-[var(--cream-deep)]/60 editorial">
    <div className="max-w-6xl mx-auto px-6 lg:px-10">
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05]" data-testid="eu-proof-headline">
        Real support for <em className="text-[var(--terracotta)]">a real move.</em>
      </h2>

      {/* Hannah & Greg — featured with photo */}
      <div className="mt-12 grid lg:grid-cols-5 gap-6 rounded-[2rem] overflow-hidden bg-[var(--navy)] text-[var(--cream)] border border-[var(--gold)]/30 shadow-2xl" data-testid="eu-testimonial-hannah-greg">
        <div className="lg:col-span-2 relative min-h-[340px] lg:min-h-0">
          <img
            src={PHOTOS.hannah_greg}
            alt="Greg, Hannah and their two children at the airport on their way to Barcelona"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--navy)]/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2 text-xs uppercase tracking-[0.25em]">
            <span className="w-2 h-2 rounded-full bg-[var(--gold)]" />
            Off to Barcelona
          </div>
        </div>
        <figure className="lg:col-span-3 p-8 lg:p-12 flex flex-col justify-center">
          <Quote className="w-9 h-9 text-[var(--gold)]" />
          <blockquote className="mt-5 font-serif text-2xl lg:text-3xl leading-[1.35]">
            "That's exactly what we want. Just{" "}
            <em className="text-[var(--gold)] not-italic font-medium">peace of mind</em> and telling
            us what we need to do."
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-[var(--cream)]/15">
            <div className="font-medium text-lg">Greg &amp; Hannah</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--gold)] mt-1">
              Moved to Barcelona with their two children
            </div>
          </figcaption>
        </figure>
      </div>

      {/* Carl & Teresa */}
      <figure className="mt-6 rounded-[2rem] p-8 lg:p-12 bg-white border border-black/5 shadow-xl relative overflow-hidden" data-testid="eu-testimonial-carl-teresa">
        <div className="absolute -top-10 -right-10 w-56 h-56 bg-[var(--gold)]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <Quote className="w-9 h-9 text-[var(--terracotta)]" />
          <blockquote className="mt-5 font-serif text-lg lg:text-xl leading-[1.55] text-[var(--navy)]">
            "Carley is very knowledgeable and passionate in assisting people to relocate to Spain.
            We had a very constructive 1:1 meeting with Carley and as the result we intend on
            relocating to Spain in January 2027, using Carley's service and assistance. Carley has
            already highlighted significant hurdles for my wife and I to clear but with Carley's
            guidance and strategy going forward I am confident we will be resident in España in
            2027. <em className="text-[var(--terracotta)]">Muchas gracias.</em>"
          </blockquote>
          <figcaption className="mt-8 pt-6 border-t border-black/10">
            <div className="font-medium text-lg text-[var(--navy)]">Carl &amp; Teresa</div>
            <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] mt-1">
              Relocating to Spain in 2027
            </div>
          </figcaption>
        </div>
      </figure>
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
const FAQS = [
  {
    q: "Does an EU passport mean I can simply arrive and live in Spain?",
    a: "An EU, EEA or Swiss passport may give you rights to live in Spain, but practical registration requirements can still apply. The relevant steps depend on factors such as work, finances, healthcare, family circumstances and intended length of stay. EU Approved helps you prepare for the process that appears relevant to your situation; it is not a guarantee of an authority's decision.",
  },
  {
    q: "What if only my spouse or partner holds the EU passport?",
    a: "Mixed-nationality couples can have additional relationship, document and family-member requirements. EU Approved helps you understand the likely sequence and prepare the supporting evidence, with specialist legal advice recommended where needed.",
  },
  {
    q: "Can we sort everything after we arrive?",
    a: "Some steps happen in Spain, but important documents may need to be obtained, translated or apostilled before you leave. Preparing early can reduce the risk of delays and costly requests from abroad.",
  },
  {
    q: "Do we need a permanent address before moving?",
    a: "Accommodation and local registration are connected. What is accepted can vary by location and arrangement, so this should be considered before relying on short-term accommodation.",
  },
  {
    q: "How does healthcare work?",
    a: "The appropriate route may involve an S1, public entitlements or suitable private cover, depending on your circumstances. Travel insurance is not automatically the same as cover for living in Spain. EU Approved can help you identify the questions to resolve and introduce relevant providers where available.",
  },
  {
    q: "How much money do we need to show?",
    a: "Requirements and evidence can depend on circumstances and the authority handling the registration. EU Approved focuses on helping you prepare a robust, organised evidence pack rather than advertising one universal number.",
  },
  {
    q: "Does EU Approved include tax advice?",
    a: "No. Get to Spain does not provide regulated tax advice. The planning process can help identify when your move date, family ties, work or property sale may make specialist cross-border tax advice important, and Carley can introduce relevant professionals where available.",
  },
  {
    q: "Can Carley help if we are moving outside Barcelona?",
    a: "Yes. EU Approved is designed as a remote support service for people moving across Spain. Local or regulated work may be referred to an appropriate provider where available.",
  },
  {
    q: "I've already completed much of the research. Is the programme still useful?",
    a: "It can be. The value is not another pile of generic information. It is checking your preparation, identifying gaps, sequencing the work and helping you know what comes next.",
  },
  {
    q: "When should we begin?",
    a: "Planning can begin well before the move. Some documents should only be ordered closer to the relevant appointment, so the purpose of starting early is to understand the timeline rather than completing everything immediately.",
  },
  {
    q: "Does EU Approved guarantee residency?",
    a: "No service can control the decision of a public authority. EU Approved provides planning, preparation and practical support designed to reduce avoidable mistakes and help you attend relevant appointments better prepared.",
  },
];

const FaqItem = ({ q, a, i }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white border border-black/5 overflow-hidden" data-testid={`eu-faq-item-${i}`}>
      <button
        className="w-full flex items-center justify-between gap-4 p-6 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`eu-faq-toggle-${i}`}
      >
        <span className="font-serif text-lg lg:text-xl text-[var(--navy)] leading-snug">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[var(--terracotta)] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-6 pb-6 text-[var(--ink-soft)] leading-relaxed" data-testid={`eu-faq-answer-${i}`}>
          {a}
        </div>
      )}
    </div>
  );
};

const Faq = () => (
  <section id="faq" className="py-24 lg:py-32 editorial">
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Questions people actually ask</div>
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]" data-testid="eu-faq-headline">
        EU Approved, <em>answered.</em>
      </h2>
      <div className="mt-12 space-y-4">
        {FAQS.map((f, i) => (
          <FaqItem key={i} q={f.q} a={f.a} i={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- FREE TOOL BAND ---------------- */
const EuToolBand = () => (
  <section className="py-20 lg:py-24 bg-[var(--cream-deep)]/60 editorial" data-testid="eu-tool-band">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 rounded-[2rem] p-8 lg:p-12 bg-white border border-black/5 shadow-sm flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">Free · 2 minutes</div>
        <h2 className="font-serif text-3xl lg:text-4xl text-[var(--navy)] mt-4 leading-[1.1]">
          Not ready to talk yet? Take the free{" "}
          <em className="text-[var(--terracotta)]">EU Passport Readiness Checklist.</em>
        </h2>
        <p className="mt-4 text-[var(--ink-soft)] leading-relaxed max-w-xl">
          7 short questions about your passport, timeline, healthcare and plans. Get a personalised
          readiness score and a clear next step — no pressure, no obligation.
        </p>
      </div>
      <a
        href="/eu-readiness"
        className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--navy)] text-[var(--cream)] font-medium flex-shrink-0"
        data-testid="eu-tool-band-cta"
      >
        Start the checklist <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </section>
);

/* ---------------- FINAL CTA ---------------- */
const FinalCTA = () => (
  <section className="py-20 lg:py-24 bg-[var(--cream-deep)] editorial" data-testid="eu-final-cta">
    <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
      <h2 className="font-serif text-4xl lg:text-5xl text-[var(--navy)] leading-[1.05]">
        Move to Spain without <em className="text-[var(--terracotta)]">guessing your way through the process.</em>
      </h2>
      <p className="mt-6 text-[var(--ink-soft)] text-lg max-w-2xl mx-auto">
        Tell us a little about your passport situation and plans. We will show you the most useful
        next step for where you are right now.
      </p>
      <a
        href="#book"
        className="pill mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
        data-testid="eu-final-cta-btn"
      >
        Plan my move to Spain <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </section>
);

/* ---------------- PAGE ---------------- */
export default function EuApproved() {
  usePageMeta(
    "EU Approved | Move to Spain with an EU or Irish Passport",
    "A personalised relocation service for EU citizens and their families moving to Spain. Plan documents, healthcare, appointments and residency steps."
  );
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] selection:bg-[var(--terracotta)] selection:text-[var(--cream)]" data-testid="eu-page">
      <SiteNav />
      <Hero />
      <RealityCheck />
      <BuyerLanguage />
      <CoreProposition />
      <SequenceVisual />
      <Journey />
      <DiyAndTrigger />
      <Circumstances />
      <RemoteAndCost />
      <About />
      <Proof />
      <Faq />
      <EuToolBand />
      <FinalCTA />
      <BookSection
        url={EU_BOOKING_URL}
        eyebrow="Your EU Approved next step"
        headline={
          <>
            Let's map what your move
            <br />
            <em>to Spain could look like.</em>
          </>
        }
        lede="A relaxed eligibility and next-steps call — a chance to discuss your passport and family circumstances, timing and the areas that currently feel unclear."
      />
      <SiteFooter />
      <FloatingBookCTA href="#book" label="Plan my move" />
    </main>
  );
}
